import React from 'react';
import { ArrowLeft, Shield, Lock, CreditCard, CheckCircle2, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="bg-[#fcfcfc] min-h-screen py-6 sm:py-10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top Header Card */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-xs mb-6 sm:mb-8">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-xs sm:text-sm font-bold text-gray-500 hover:text-red-600 transition mb-4 cursor-pointer"
          >
            <ArrowLeft size={16} /> Back
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100">
              <Shield size={24} />
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-extrabold text-gray-900">Privacy Policy</h1>
              <p className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                How Lokanatya.com protects and manages your personal & payment data
              </p>
            </div>
          </div>
        </div>

        {/* Content Body */}
        <div className="bg-white rounded-3xl p-6 sm:p-10 border border-gray-100 shadow-xs space-y-8">
          
          {/* Section 1 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 text-xs font-black flex items-center justify-center">1</span>
              Introduction
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed font-normal">
              When you make a purchase on <strong>Lokanatya.com</strong>, your payment is processed securely through a trusted third-party payment gateway. This section explains how your personal data and payment information are handled, stored, and protected.
            </p>
          </div>

          {/* Section 2 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 text-xs font-black flex items-center justify-center">2</span>
              Collection of Payment Information
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-3">
              When buying tickets or making payments on Lokanatya, you may be requested to provide:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs sm:text-sm font-bold text-gray-800 mb-4">
              <li className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-blue-500 shrink-0" />
                Credit/Debit Card Number
              </li>
              <li className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-blue-500 shrink-0" />
                Card Expiration Date
              </li>
              <li className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-blue-500 shrink-0" />
                CVV / Security Code
              </li>
            </ul>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
              This information is collected directly by our payment gateway provider and is <strong>never stored on Lokanatya servers</strong>.
            </p>
          </div>

          {/* Section 3 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 text-xs font-black flex items-center justify-center">3</span>
              Payment Gateway Provider
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
              We use <strong>Razorpay</strong>, a secure and PCI-DSS compliant payment service, to process all transactions. Razorpay follows strict security standards to ensure end-to-end protection for all payments.
            </p>
          </div>

          {/* Section 4 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 text-xs font-black flex items-center justify-center">4</span>
              Security of Payment Information
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
              Razorpay uses strong 128/256-bit SSL/TLS encryption technology to safeguard your payment data during transit. We do not store any sensitive card details on our infrastructure.
            </p>
          </div>

          {/* Section 5 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 text-xs font-black flex items-center justify-center">5</span>
              How We Use Payment Information
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-3">
              Your payment details are used strictly for:
            </p>
            <ul className="space-y-2 text-xs sm:text-sm text-gray-700 font-medium">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                Verifying your payment authorization
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                Completing your ticket booking transaction
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                Processing refunds or adjustments if applicable
              </li>
            </ul>
          </div>

          {/* Section 6 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 text-xs font-black flex items-center justify-center">6</span>
              Data Sharing and Disclosure
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
              Your payment information is shared only with <strong>Razorpay</strong> for transaction fulfillment. We do not sell, rent, or share your payment details with any third parties unless required by law.
            </p>
          </div>

          {/* Section 7 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 text-xs font-black flex items-center justify-center">7</span>
              Your Rights
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
              You have the right to access or update your profile details at any time in your <strong>Profile Settings</strong>. If you have questions or concerns about data privacy, feel free to contact our support team.
            </p>
          </div>

          {/* Section 8 */}
          <div>
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 text-xs font-black flex items-center justify-center">8</span>
              Changes to This Policy
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
              We may update this Privacy Policy periodically. Any modifications will be posted here on our website along with the updated revision date.
            </p>
          </div>

        </div>

      </div>
    </div>
  );
}
